import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Download, Play } from "lucide-react";
import { api } from "@/lib/api";

const toEmbed = (url) => {
  if (!url) return "";
  const shortMatch = url.match(/youtu\.be\/([\w-]+)/);
  if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}?rel=0`;
  const longMatch = url.match(/youtube\.com\/watch\?v=([\w-]+)/);
  if (longMatch) return `https://www.youtube.com/embed/${longMatch[1]}?rel=0`;
  return url;
};

// Current worksheet — swap when new PDF is uploaded
const WORKSHEET_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_session-booking-23/artifacts/uxr63792_Pause_the_Spiral_Jess_Eisaman_Co.pdf%20%282%29.pdf";

const SacredResetWatch = () => {
  const [videoUrl, setVideoUrl] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/pause-the-spiral/config")
      .then((r) => setVideoUrl(r.data?.video_url || ""))
      .catch(() => {});
  }, []);

  return (
    <main className="bg-[#F6F1EA]" data-testid="watch-page">
      {/* You're in */}
      <section className="pt-28 pb-8 sm:pt-32 sm:pb-10">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-6">A quiet arrival</p>
          <h1 className="font-serif text-5xl sm:text-6xl text-[#4F5D4F] leading-[1.05] tracking-tight" data-testid="watch-heading">
            <em className="italic text-[#B89B63]">You're in.</em>
          </h1>
          <div className="mt-8 space-y-4 text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-relaxed">
            <p>
              Before you open another tab, Google another question, or listen to another
              podcast—<strong className="font-medium text-[#4F5D4F]">give yourself these ten minutes.</strong>
            </p>
            <p>You don't need to figure everything out today.</p>
            <p className="font-serif italic text-xl text-[#4F5D4F]">Your guided experience is ready for you.</p>
          </div>
        </div>
      </section>

      {/* Video */}
      <section className="pb-10">
        <div className="max-w-3xl mx-auto px-6">
          <div className="relative aspect-video w-full bg-[#4F5D4F] shadow-lg" data-testid="watch-video-frame">
            {videoUrl ? (
              <iframe
                src={toEmbed(videoUrl)}
                title="Pause the Spiral"
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-[#F6F1EA]/70 gap-3">
                <Play size={22} /> Loading your experience…
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Download */}
      <section className="pb-14 sm:pb-16">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-3">Your companion</p>
          <h2 className="font-serif text-3xl text-[#4F5D4F] mb-4">Download the workbook</h2>
          <p className="text-[#4F5D4F]/85 font-light leading-relaxed mb-8 max-w-lg mx-auto">
            Print it, sit with it, and let it walk you through the same 5-step practice as
            the video—at your own pace.
          </p>
          <a
            href={WORKSHEET_URL}
            download
            className="group inline-flex items-center gap-3 bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] px-8 py-4 text-xs tracking-eyebrow uppercase transition-colors"
            data-testid="watch-download-cta"
          >
            <Download size={14} /> Download the workbook
          </a>
          <p className="text-xs text-[#8C9A88] italic mt-4">Also sent to your email · Yours to keep, come back to anytime.</p>
        </div>
      </section>

      {/* Soft next step — Reclaim Session */}
      <section className="py-14 sm:py-16 border-t border-[#D7B2A3]/30">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-4">When you're ready · No rush</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#4F5D4F] leading-tight tracking-tight mb-5">
            If one question keeps circling...
          </h2>
          <p className="text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-relaxed max-w-xl mx-auto mb-8">
            When you've finished the reset and something surfaces you can't stop thinking
            about, that's when <strong className="font-medium text-[#4F5D4F]">Reclaiming You</strong> is here.
            A private 60-minute Faith &amp; Identity Coaching session for the one thing you can't stop circling.
          </p>
          <Link
            to="/reclaim"
            className="group inline-flex items-center gap-3 border border-[#4F5D4F] text-[#4F5D4F] hover:bg-[#4F5D4F] hover:text-[#F6F1EA] px-7 py-4 text-xs tracking-eyebrow uppercase transition-colors"
            data-testid="watch-reclaim-cta"
          >
            Learn about Reclaiming You <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default SacredResetWatch;
